<template>
  <div class="panel panel-content">
    <div class="panel-title q-pb-xs row justify-between">
      <div class="self-center">Reproducció de vídeos geolocalitzats</div>
      <div class="self-center">
        <slot name="title-right" />
      </div>
    </div>
    <div v-if="fetchingPlaylist"><q-spinner size="lg"/></div>
    <div v-else-if="!playlist || playlist.length === 0">
      <span v-show="noPlaylistFound">
        {{ $t('tools.geovideos.description') }}
      </span>
      <span v-html="$t('tools.geovideos.clickMapDescription')" />
    </div>
    <div v-else class="video-container">
      <div class="row q-mb-sm">
        <div>
          <q-btn-dropdown
            flat
            dense
            color="black"
            size="lg"
            :label="`${playlist[currentVideoIndex].name} ${formatDate(playlist[currentVideoIndex].date)}`"
          >
            <q-list>
              <q-item
                v-for="(video, index) in playlist" :key="video.url"
                clickable v-close-popup
                @click="currentVideoIndex = index"
              >
                <q-item-section>
                  <q-item-label>{{ video.name }} {{ formatDate(video.date) }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-btn-dropdown>
        </div>
        <q-space />
        <div class="self-center">
          <q-btn v-if="properties.length > 0" flat dense icon="las la-info-circle" @click="showAdditionalInfo = true">
            <q-tooltip>{{ $t('tools.geovideos.showAdditionalInfo') }}</q-tooltip>
            <q-dialog v-model="showAdditionalInfo" v-if="showAdditionalInfo">
              <q-card class="q-pa-sm q-pb-md" style="min-width: 400px">
                <q-card-section class="row items-center q-py-md">
                  <q-icon name="las la-info-circle" style="font-size: 1.2em;" class="q-pr-sm"/>
                  <div class="text-title q-pr-lg">{{ $t('tools.geovideos.additionalInfo') }}</div>
                  <q-space />
                  <q-btn icon="close" flat round dense v-close-popup />
                </q-card-section>
                <q-card-section class="q-py-xs">
                  <div v-for="property in properties" :key="property.name">
                    <b class="text-subtitle2">{{ property.name }}: </b>
                    <span class="text-subtitle">{{ property.value }}</span>
                  </div>
                </q-card-section>
              </q-card>
            </q-dialog>
          </q-btn>
          <q-btn flat dense :icon="centerMap ? 'location_off' : 'location_on'" @click="switchCenterMap" class="q-mx-sm">
            <q-tooltip>{{ centerMap ? $t('tools.geovideos.deactivateCenterMap') : $t('tools.geovideos.activateCenterMap') }}</q-tooltip>
          </q-btn>
          <q-btn flat dense icon="las la-camera" class="q-mr-md" @click="captureFrame">
            <q-tooltip>{{ $t('tools.geovideos.takePicture') }}</q-tooltip>
          </q-btn>
          <select
            onchange="document.getElementById('geovideo').playbackRate = this.value"
          >
            <option value="0.5">x0.5</option>
            <option value="1" selected>x1</option>
            <option value="1.5">x1.5</option>
            <option value="2">x2</option>
            <option value="4">x4</option>
            <q-tooltip>{{ $t('tools.geovideos.reproductionSpeed') }}</q-tooltip>
          </select>
        </div>
      </div>
      <div class="text-center">
        <video
          ref="videoPlayer"
          id="geovideo"
          crossOrigin="anonymous"
          controls
          :src="playlist[currentVideoIndex].url"
          style="width: 100%;"
          @timeupdate="onVideoTimeUpdate($event, playlist[currentVideoIndex])"
        >
        </video>
        <div class="flex-center row q-pb-lg">
          <div class="self-center">
            <q-btn dense flat round icon="chevron_left"  @click="moveBackwards">
              <q-tooltip>{{ $t('names.moveBackwards') }}</q-tooltip>
            </q-btn>
          </div>
          <div class="self-center q-px-md">
            <q-input
              :hint="$t('tools.geovideos.stepForwardBackward')"
              dense
              v-model="step"
              step="1"
              style="width: 110px;"
            />
          </div>
          <div class="self-center">
            <q-btn dense flat round icon="chevron_right" @click="moveForward">
              <q-tooltip>{{ $t('names.moveForward') }}</q-tooltip>
            </q-btn>
          </div>
        </div>
        <div v-show="!!currentPk">
          <b>PK teòric:</b> {{ currentPk }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { ClosePopup, QBtn, QBtnDropdown, QCard, QCardSection, QDialog, QIcon, QItem, QItemLabel, QItemSection, QInput, QList, QSpace, QSpinner, QTooltip } from 'quasar'
import { lib } from '@giscube/geoportal'
const L = lib.L

export default {
  name: 'GeoVideoPanel',
  directives: {
    ClosePopup
  },
  components: {
    QBtn,
    QBtnDropdown,
    QCard,
    QCardSection,
    QDialog,
    QIcon,
    QItem,
    QItemLabel,
    QItemSection,
    QInput,
    QList,
    QSpace,
    QSpinner,
    QTooltip
  },
  data () {
    return {
      showAdditionalInfo: false,
      currentMarker: null,
      currentPk: null,
      videoIntervals: {},
      highlightLinestring: null,
      isDraggingMarker: false,
      p: null,
      videoElement: null
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      if (to.params.p) {
        setTimeout(() => {
          const latlngParams = to.params.p.split(',')
          if (latlngParams && latlngParams.length > 0) {
            const latlng = {
              lat: latlngParams[0],
              lng: latlngParams[1]
            }
            vm.fetchPlaylist(latlng)
          }
          vm.$router.replace({ params: { p: null } }).catch(() => {})
        }, 600)
      }
    })
  },
  computed: {
    map () {
      return this.$store.state.map.mapObject
    },
    // Mapejar el estado del store a las propiedades del componente
    ...mapState('geovideos', {
      centerMap: state => state.centerMap,
      fetchingPlaylist: state => state.fetchingPlaylist,
      noPlaylistFound: state => state.noPlaylistFound,
      playlist: state => state.playlist
    }),
    currentVideoIndex: {
      get () {
        return this.$store.state.geovideos.currentVideoIndex
      },
      set (value) {
        this.$store.commit('geovideos/setCurrentVideoIndex', value)
      }
    },
    currentVideoTime: {
      get () {
        return this.$store.state.geovideos.currentVideoTime
      },
      set (value) {
        this.$store.commit('geovideos/setCurrentVideoTime', value)
      }
    },
    step: {
      get () {
        return this.$store.state.geovideos.step
      },
      set (value) {
        this.$store.commit('geovideos/setStep', value)
      }
    },
    properties () {
      return this.playlist.length > 0 ? this.playlist[this.currentVideoIndex].properties : null
    }
  },
  watch: {
    currentVideoIndex (_, oldIndex) {
      // Parar el video anterior
      if (oldIndex !== null && this.videoElement) {
        this.pauseVideo()
      }

      let second = 0
      if (this.currentMarker) {
        const coords = this.currentMarker.getLatLng()
        second = this.getSecondForCoordinates(coords, this.currentVideoIndex)
      }

      setTimeout(() => {
        this.videoElement = document.getElementById('geovideo')
        if (!this.videoElement) return
        this.$store.commit('geovideos/setCurrentVideoTime', second)
        this.videoElement.currentTime = this.currentVideoTime
        this.updateHighlightLinestring()
        this.updateMapMarker()
      }, 400)
    },
    map () {
      this.mapChanged()
    },
    centerMap () {
      if (this.centerMap && this.currentMarker) {
        this.map.setView(this.currentMarker.getLatLng())
      }
    }
  },
  mounted () {
    this.$store.commit('setCurrentTool', 'geovideo')
    this.mapChanged()
    setTimeout(() => {
      this.videoElement = document.getElementById('geovideo')
      if (!this.videoElement) return
      this.videoElement.currentTime = this.currentVideoTime
      this.updateHighlightLinestring()
      this.updateMapMarker()
    }, 300)
  },
  updated () {
    this.updateVideoElementListeners()
  },
  methods: {
    addVideoEventListeners () {
      const video = this.videoElement
      if (!video) return
      video.addEventListener('play', this.startMarkerAnimation)
      video.addEventListener('pause', this.stopMarkerAnimation)
      video.addEventListener('seeking', this._onVideoSeeking)
      this._attachedVideoElement = video
      this._videoListenersAttached = true
    },
    removeVideoEventListeners () {
      const video = this._attachedVideoElement || this.videoElement
      if (!video) return
      video.removeEventListener('play', this.startMarkerAnimation)
      video.removeEventListener('pause', this.stopMarkerAnimation)
      video.removeEventListener('seeking', this._onVideoSeeking)
      this._videoListenersAttached = false
      this._attachedVideoElement = null
    },
    updateVideoElementListeners () {
      this.removeVideoEventListeners()
      this.addVideoEventListeners()
    },
    pauseVideo () {
      if (this.videoElement) {
        this.videoElement.pause()
      }
    },
    switchCenterMap () {
      this.$store.commit('geovideos/setCenterMap', !this.centerMap)
    },
    removeHighlightLinestring () {
      if (this.highlightLinestring && this.map) {
        this.map.removeLayer(this.highlightLinestring)
        this.highlightLinestring = null
      }
    },
    addHighlightLinestring () {
      if (this.currentVideoIndex !== null && this.playlist[this.currentVideoIndex] && this.map) {
        const coordinatesList = this.playlist[this.currentVideoIndex].coordinatesSeconds.map(item => [item.coordinates[1], item.coordinates[0]])
        this.highlightLinestring = L.polyline(coordinatesList, {
          color: '#FFD700',
          weight: 5,
          opacity: 1
        }).addTo(this.map)
      }
    },
    updateHighlightLinestring () {
      this.removeHighlightLinestring()
      this.addHighlightLinestring()
    },
    onVideoPause () {
      // stop any running animation loop
      this.stopMarkerAnimation()
      if (this.videoIntervals[this.currentVideoIndex]) {
        clearInterval(this.videoIntervals[this.currentVideoIndex])
      }
    },
    onVideoTimeUpdate (event, video) {
      // Use the exact current time for smooth interpolation
      this.updateMapMarker()
      this.$store.commit('geovideos/setCurrentVideoTime', event.target.currentTime)
      this.setPKForTime(video)
    },
    getCoordinatesForTime (video, time) {
      if (!video || !video.coordinatesSeconds || video.coordinatesSeconds.length === 0) return null
      const arr = video.coordinatesSeconds
      if (time <= arr[0].second) return arr[0].coordinates
      if (time >= arr[arr.length - 1].second) return arr[arr.length - 1].coordinates

      for (let i = 0; i < arr.length - 1; i++) {
        const A = arr[i]
        const B = arr[i + 1]
        if (time >= A.second && time <= B.second) {
          const dt = B.second - A.second
          if (dt === 0) return A.coordinates
          const t = (time - A.second) / dt
          const x = A.coordinates[0] + t * (B.coordinates[0] - A.coordinates[0])
          const y = A.coordinates[1] + t * (B.coordinates[1] - A.coordinates[1])
          return [x, y]
        }
      }
      return null
    },
    setPKForTime (video) {
      if (!video.pkStart) {
        this.currentPk = null
        return
      }
      const time = this.currentVideoTime
      const PKmeters = (pk) => {
        let meters = Math.trunc((pk - Math.trunc(pk)) * 1000)
        meters = meters.toString()
        while (meters.length < 3) meters = '0' + meters
        return meters
      }
      const formatPK = (pk) => `${Math.trunc(pk)}+${PKmeters(pk)}`
      const distanceToPk = (distance) => (video.pkStart * 1000 + distance) / 1000
      const velocity = (A, B) => (B.distance - A.distance) / (B.second - A.second)

      if (!video || !video.coordinatesSeconds || video.coordinatesSeconds.length === 0) return null
      const arr = video.coordinatesSeconds
      if (time <= arr[0].second) return distanceToPk(arr[0].distance)
      if (time >= arr[arr.length - 1].second) return distanceToPk(arr[arr.length - 1].distance)

      for (let i = 0; i < arr.length - 1; i++) {
        const A = arr[i]
        const B = arr[i + 1]
        if (time >= A.second && time <= B.second) {
          const dt = B.second - A.second
          if (dt === 0) {
            this.currentPk = formatPK(distanceToPk(A.distance))
            return
          }
          const distance = A.distance + velocity(A, B) * (time - A.second)
          this.currentPk = formatPK(distanceToPk(distance))
          return
        }
      }
      this.currentPk = null
    },
    getSecondForCoordinates (latlng, videoIndex) {
      if (!this.playlist || this.playlist.length <= videoIndex || !this.playlist[videoIndex]) return null
      const coordinatesSeconds = this.playlist[videoIndex].coordinatesSeconds
      let closestSecond = null
      let minDistance = Infinity

      coordinatesSeconds.forEach(item => {
        const distance = Math.sqrt(
          Math.pow(item.coordinates[1] - latlng.lat, 2) +
          Math.pow(item.coordinates[0] - latlng.lng, 2)
        )
        if (distance < minDistance) {
          minDistance = distance
          closestSecond = item.second
        }
      })

      return closestSecond
    },
    getCurrentAndNextSecondCoordinates () {
      const videoIndex = this.currentVideoIndex
      if (!this.playlist || this.playlist.length <= videoIndex || !this.playlist[videoIndex]) return
      const currentTime = this.currentVideoTime
      const coordSecs = this.playlist[videoIndex].coordinatesSeconds
      let index = coordSecs.findIndex((curr, i, arr) => {
        if (i === arr.length - 1) return false
        const next = arr[i + 1]
        if (!next) return false
        return currentTime >= curr.second && currentTime <= next.second
      })
      if (index === -1) {
        if (coordSecs[0].second > currentTime) {
          index = 0
        } else if (coordSecs[coordSecs.length - 1].second < currentTime) {
          index = coordSecs.length - 2
        } else {
          return { curr: null, next: null }
        }
      }
      const curr = coordSecs[index]
      const next = coordSecs[index + 1]
      return { curr, next }
    },
    getSecondForDistance (distance) {
      const videoIndex = this.currentVideoIndex
      if (!this.playlist || this.playlist.length <= videoIndex || !this.playlist[videoIndex]) return
      const coordSecs = this.playlist[videoIndex].coordinatesSeconds
      let index = coordSecs.findIndex((curr, i, arr) => {
        if (i === arr.length - 1) return false
        const next = arr[i + 1]
        if (!next) return false
        return distance >= curr.distance && distance <= next.distance
      })
      if (index === -1) {
        if (coordSecs[0].distance > distance) {
          index = 0
        } else if (coordSecs[coordSecs.length - 1].distance < distance) {
          index = coordSecs.length - 2
        } else {
          return { curr: null, next: null }
        }
      }
      const curr = coordSecs[index]
      const next = coordSecs[index + 1]
      const t = (distance - curr.distance) / (next.distance - curr.distance)
      const x = curr.coordinates[0] + t * (next.coordinates[0] - curr.coordinates[0])
      const y = curr.coordinates[1] + t * (next.coordinates[1] - curr.coordinates[1])
      const latlng = {
        lat: y,
        lng: x
      }
      return this.getSecondForCoordinates(latlng, videoIndex)
    },
    setSecondAddingDistance (distance) {
      const { curr, next } = this.getCurrentAndNextSecondCoordinates()
      if (!curr || !next) return
      if (curr.reversed) distance = -distance
      const velocity = (next.distance - curr.distance) / (next.second - curr.second)
      const currDistance = curr.distance + velocity * (this.currentVideoTime - curr.second)
      const newDistance = currDistance + distance
      const newSecond = this.getSecondForDistance(newDistance)
      this.videoElement.currentTime = newSecond
      this.$store.commit('geovideos/setCurrentVideoTime', newSecond)
      this.updateMapMarker()
    },
    moveBackwards () {
      this.setSecondAddingDistance(-1 * this.step)
    },
    moveForward () {
      this.setSecondAddingDistance(1 * this.step)
    },
    updateMapMarker () {
      const second = this.currentVideoTime
      const coordinates = this.getCoordinatesForTime(this.playlist[this.currentVideoIndex], second)

      if (!second || !coordinates || coordinates.length !== 2) return

      const latlng = [coordinates[1], coordinates[0]]

      if (this.currentMarker) {
        // Move existing marker for smoothness
        this.currentMarker.setLatLng(latlng)
        this.currentMarker.bringToFront()
      } else {
        this.currentMarker = L.circleMarker(
          latlng,
          {
            radius: 8,
            fillColor: '#FF6B6B',
            color: '#C92A2A',
            weight: 2,
            opacity: 0.8,
            fillOpacity: 0.7
          }
        ).on('click', (e) => {
          L.DomEvent.stopPropagation(e)
        }).addTo(this.map)

        // Afegir funcionalitat per arrossegar el marcador
        this.setupMarkerDragging()
      }
      if (this.centerMap && this.currentMarker) {
        this.map.setView(this.currentMarker.getLatLng())
      }
    },
    setupMarkerDragging () {
      if (!this.currentMarker) return

      this.currentMarker.on('mousedown', (e) => {
        this.isDraggingMarker = true

        const onMouseMove = (moveEvent) => {
          if (!this.isDraggingMarker) return
          this.pauseVideo()

          const latlng = moveEvent.latlng
          const layerPoint = this.map.latLngToLayerPoint(latlng)
          const closestLayerPoint = this.highlightLinestring.closestLayerPoint(layerPoint)
          const closestLatLng = this.map.layerPointToLatLng(closestLayerPoint)
          this.currentMarker.setLatLng(closestLatLng)
        }

        const onMouseUp = (e) => {
          this.map.off('mousemove', onMouseMove)
          this.map.off('mouseup', onMouseUp)
          this.map.dragging.enable()
          const videoIndex = this.currentVideoIndex
          // Buscar el segon corresponent a aquesta coordenada
          const latLng = this.currentMarker.getLatLng()
          const second = this.getSecondForCoordinates(latLng, videoIndex)
          // Actualitzar el vídeo al segon corresponent
          if (second !== null) {
            if (this.videoElement) {
              this.videoElement.currentTime = second
              this.$store.commit('geovideos/setCurrentVideoTime', second)
            }
          }
          setTimeout(() => {
            this.isDraggingMarker = false
          }, 200)
        }

        this.map.dragging.disable()
        this.map.on('mousemove', onMouseMove)
        this.map.on('mouseup', onMouseUp)
      })
    },
    startMarkerAnimation () {
      if (this._rafId) return
      const animate = () => {
        if (!this.videoElement || this.currentVideoIndex === null) {
          this._rafId = null
          return
        }
        this.updateMapMarker()
        this._rafId = requestAnimationFrame(animate)
      }
      this._rafId = requestAnimationFrame(animate)
    },
    stopMarkerAnimation () {
      if (this._rafId) {
        cancelAnimationFrame(this._rafId)
        this._rafId = null
      }
    },
    _onVideoSeeking (event) {
      const video = event && event.target ? event.target : this.videoElement
      if (!video || this.currentVideoIndex === null) return
      this.$store.commit('geovideos/setCurrentVideoTime', video.currentTime)
      this.updateMapMarker()
    },
    clearMapMarker () {
      if (this.currentMarker && this.map) {
        this.map.removeLayer(this.currentMarker)
        this.currentMarker = null
      }
      this.isDraggingMarker = false
    },
    formatDate (date) {
      if (!date) {
        return ''
      }
      date = date.split('-')
      date.reverse()
      return `(${date.join('/')})`
    },
    async mapChanged () {
      if (!this.map || !this.map.giscube) {
        return
      }
      this.map.on('click', function (event) {
        if (!this.isDraggingMarker) {
          this.fetchPlaylist(event.latlng)
        }
      }.bind(this))
    },
    fetchPlaylist (latlng) {
      this.$store.dispatch('geovideos/fetchPlaylist', latlng)
        .then(() => {
          setTimeout(() => {
            this.videoElement = document.getElementById('geovideo')
            if (!this.videoElement) return
            const second = this.getSecondForCoordinates(latlng, this.currentVideoIndex)
            this.$store.commit('geovideos/setCurrentVideoTime', second)
            this.videoElement.currentTime = this.currentVideoTime
            this.updateHighlightLinestring()
            this.updateMapMarker()
          }, 500)
        })
    },
    captureFrame () {
      if (!this.videoElement) return
      const canvas = document.createElement('canvas')
      canvas.width = this.videoElement.videoWidth
      canvas.height = this.videoElement.videoHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(this.videoElement, 0, 0, canvas.width, canvas.height)
      const dataURL = canvas.toDataURL('image/png')

      // Crear un enlace para descargar la imagen
      const link = document.createElement('a')
      link.href = dataURL
      link.download = `geovideo_${Date.now()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  },
  beforeDestroy () {
    this.clearMapMarker()
    this.stopMarkerAnimation()
    this.removeVideoEventListeners()
    this.removeHighlightLinestring()
    this.$store.commit('setCurrentTool', null)
    this.map.off('click', this.fetchPlaylist)
  }
}
</script>

<style scoped>

</style>
